import { pipe } from "fp-ts/lib/function"
import type { Json } from "fp-ts/lib/Json"
import type { AtomEffect } from "recoil"
import { atom } from "recoil"

import IS_DEV from "~/utils/isDev"
import IS_QA from "~/utils/isQa"

export type SerializationInterface<T> = {
  serialize: (t: T) => string
  deserialize: (s: string) => T
}

export const localStorageSerializationEffect: <T>(
  key: string,
  serializationInterface: SerializationInterface<T>,
) => AtomEffect<T> =
  (key, { serialize, deserialize }) =>
  ({ setSelf, onSet }) => {
    const savedValue = localStorage.getItem(key)

    if (savedValue != null) setSelf(pipe(savedValue, deserialize))

    onSet((newValue, _, isReset) => {
      isReset
        ? localStorage.removeItem(key)
        : pipe(newValue, serialize, (s) => localStorage.setItem(key, s))
    })
  }

export const localStorageEffect: <T extends Json>(
  key: string,
) => AtomEffect<T> = (key) =>
  localStorageSerializationEffect(key, {
    serialize: JSON.stringify,
    deserialize: JSON.parse,
  })

export const debugVisualizationsEnabledState = atom<Set<string>>({
  key: `debugVisualizationsEnabled`,
  default: new Set(),
  effects: [
    localStorageSerializationEffect(`debugVisualizationsEnabled`, {
      serialize: (s) => JSON.stringify(Array.from(s)),
      deserialize: (s) => new Set(JSON.parse(s)),
    }),
  ],
})
export const debugPanelIsPresentState = atom<boolean>({
  key: `debugPanelIsPresent`,
  default: IS_DEV || IS_QA,
  effects: [localStorageEffect(`debugPanelIsPresent`)],
})

export const debugPanelIsOpenState = atom<boolean>({
  key: `debugPanelIsOpen`,
  default: false,
  effects: [localStorageEffect(`debugPanelIsOpen`)],
})

export const debugOrder: string[] = [
  `SelectionSnapPoints`,
  `UnselectedSnapPoints`,
  `ActiveSnapPoint`,
  `NodeLabels`,
  `PanelLabels`,
  `PanelSelectionOutline`,
  `AutoPlacementVisualization`,
  `TracingVisualization`,
  `InsetAreaFilteringVisualizations`,
  `LinkedEdgesVisualizations`,
  `EdgeLabels`,
  `DivisionVisualization`,
]

export const debugOrderState = atom({
  key: `debugOrderState`,
  default: debugOrder,
  effects: [localStorageEffect(`debugOrderState`)],
})
